<template>
  <div v-if="data" class="d-flex flex-column align-center text-center" style="gap: 16px">
    <div>
      {{
        t("landing.paymentLinkMessage", {
          amount: displayAmount(data.amount),
          merchant: data.siteDisplayName || data.legalEntityName,
        })
      }}
    </div>
    <div v-if="receiptError" class="text-red">
      {{ receiptError }}
    </div>
    <div v-else-if="data.originalRecipientEmail && !alternateRecipient">
      <div>
        {{ t("landing.receipt.sendTo", { email: data.originalRecipientEmail }) }}
      </div>
      <div class="mt-2">
        <span
          class="text-decoration-underline cursor-pointer"
          @click="handleAlternateRecipientEmail"
          >{{ t("landing.receipt.alternateEmail") }}</span
        >
        {{ t("landing.receipt.alternateEmailSuffix") }}
      </div>
    </div>
    <div v-else class="d-flex flex-column" style="gap: 12px">
      <span>{{ t("landing.receipt.requestReceipt") }}</span>
      <v-text-field
        v-model="form.email"
        type="email"
        :placeholder="t('landing.receipt.emailPlaceholder')"
        variant="solo-filled"
        :error-messages="v$.email.$errors.map((e) => e.$message.toString())"
        clearable
      />
    </div>

    <div class="d-flex align-center" style="gap: 5px">
      <v-checkbox v-model="termsAccepted" style="display: inline-block" />
      <span class="text-left">
        <span @click="termsAccepted = !termsAccepted">
          {{
            t("landing.terms.prefix", { merchant: data.siteDisplayName || data.legalEntityName })
          }}
          <a
            :href="data.termsOfServiceUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="terms"
            >{{ t("landing.terms.link") }}</a
          >
          {{ t("landing.terms.suffix") }}
        </span>
      </span>
    </div>
    <div class="text-center">
      <v-btn
        :disabled="!termsAccepted"
        size="x-large"
        class="font-weight-bold text-primary"
        @click="handlePayNowClick"
      >
        {{ t("landing.payNow") }}
      </v-btn>
    </div>
  </div>

  <div v-else-if="error">
    <v-alert
      :color="(errorIsAnActualError ? 'red' : 'green') + ' lighten-2'"
      :type="errorIsAnActualError ? 'error' : 'success'"
      dark
    >
      <span class="font-weight-bold">{{ errorDisplayMessage }}</span
      ><br />
      <span v-if="errorIsAnActualError"
        >{{ t("landing.linkNotFound.text1") }}
        <a href="mailto:help@yetipay.me" target="_blank" style="color: #fff">help@yetipay.me</a>
        {{ t("landing.linkNotFound.text2") }}</span
      >
    </v-alert>
  </div>

  <div v-else class="text-center">
    <v-progress-circular indeterminate color="primary" />
  </div>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { email } from "@vuelidate/validators";
import BigNumber from "bignumber.js";
import { ref, computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { postPaymentLinkReceipt } from "@/api/postCustomerReceiptEmail";
import { useFetchLinkData } from "@/composables/useFetchLinkData";

const { t } = useI18n();

const linkId = useRoute().params.linkId as string;

const termsAccepted = ref(false);
const alternateRecipient = ref(false);

const form = reactive({ email: "" });
const v$ = useVuelidate({ email: { email, $autoDirty: false } }, form);
const receiptError = ref<string | undefined>();

const { data, error } = useFetchLinkData();
const errorDisplayMessage = computed(() => {
  if (error.value?.error === "Payment link not found") {
    return t("landing.linkNotFound.title");
  }
  if (error.value?.error === "Payment link already paid") {
    return t("landing.paymentLinkAlreadyPaid");
  }

  return undefined;
});

const errorIsAnActualError = computed(() => {
  if (!error) {
    return false;
  }
  if (error.value?.error === "Payment link already paid") {
    return false;
  }
  return true;
});

const handleAlternateRecipientEmail = () => {
  alternateRecipient.value = true;
};

const displayAmount = (amount: { value: number; currency: string }) => {
  return new BigNumber(amount.value).dividedBy(100).toNumber().toLocaleString("en-UK", {
    style: "currency",
    currency: amount.currency,
  });
};

const handlePayNowClick = async () => {
  if (receiptError.value) {
    openPayNowLink();
    return;
  }

  v$.value.$touch();
  if (!v$.value.$error && data.value?.internalUrl) {
    try {
      if (form.email) {
        await postPaymentLinkReceipt(linkId, form.email);
      }
      openPayNowLink();
    } catch {
      receiptError.value = t("landing.receipt.error");
    }
  }
};

const openPayNowLink = () => window.open(data.value?.internalUrl, "_self");
</script>

<style scoped>
.terms {
  color: #fff;
}
.terms:hover {
  color: #e34696;
}
</style>
